import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import Navbar from '../components/site/Navbar';
import Blog from '../components/site/Blog';
import FooterCTA from '../components/site/FooterCTA';
import Footer from '../components/site/Footer';
import LogoWatermark from '../components/site/LogoWatermark';
import { offerings } from '../data/mock';

const Pill = ({ children, dark }) => (
  <span
    className="inline-block px-4 py-1.5 rounded-md font-display uppercase tracking-wider text-sm md:text-base"
    style={{ background: '#2EF5FF', color: '#12367F' }}
  >
    {children}
  </span>
);

const OfferingsHero = () => (
  <section className="relative pt-28 md:pt-32 overflow-hidden" style={{ background: '#12367F', color: '#FFFFFF' }}>
    {/* Decorative soft "C" shape */}
    <div
      aria-hidden
      className="absolute -right-40 top-1/4 w-[55vw] max-w-[800px] aspect-square pointer-events-none opacity-[0.07] rounded-[45%]"
      style={{ background: '#2EF5FF' }}
    />
    <LogoWatermark size={520} opacity={0.07} position="left" />
    <div className="container-x px-6 pt-12 md:pt-20 pb-20 md:pb-28 relative">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
        <div className="lg:col-span-7">
          <h1
            className="font-display uppercase text-balance"
            style={{ fontSize: 'clamp(3rem, 9vw, 9.5rem)', lineHeight: 0.88 }}
          >
            <span className="text-white">Connecting</span>
            <br />
            <span className="text-white">Creators</span>
            <br />
            <span style={{ color: '#2EF5FF' }}>With Their</span>
            <br />
            <span style={{ color: '#2EF5FF' }}>Dream Brands</span>
          </h1>
        </div>
        <div className="lg:col-span-5 space-y-6 text-white/85 text-base md:text-lg leading-relaxed lg:pb-6">
          <p>
            Your ticket to more eyeballs, action and growth. We connect creators and{' '}
            <span className="underline decoration-2 underline-offset-4 decoration-[#2EF5FF]">
              micro-creators
            </span>{' '}
            with life-changing brands to help you roll out impactful social-first strategies and
            build a future you&apos;re obsessed with.
          </p>
          <p>
            Since 2020, we&apos;ve delivered &pound;40 million in revenue for our creators. With
            around-the-clock support from a full team of managers who live and breathe social,
            you&apos;re free to make your mark in front of an ever-growing audience.
          </p>
          <div className="pt-2">
            <a href="/contact" className="btn-base btn-accent">
              Enquire Now <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const OfferingsIntro = () => (
  <section className="pt-20 md:pt-28 pb-6 md:pb-10" style={{ background: '#FFFFFF' }}>
    <div className="container-x px-6 text-center">
      <Pill>Our Offerings</Pill>
      <h2
        className="font-display uppercase text-[#12367F] text-balance mt-8 max-w-5xl mx-auto"
        style={{ fontSize: 'clamp(2.25rem, 5.5vw, 5rem)', lineHeight: 0.92 }}
      >
        No one does creator marketing
        <br />
        like Nasu Management does
      </h2>
    </div>
  </section>
);

const OfferingRow = ({ item, index }) => {
  const reverse = index % 2 === 1;
  return (
    <div className="container-x px-6 py-14 md:py-20">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className={`lg:col-span-6 ${reverse ? 'lg:order-2' : ''}`}>
          <div className="relative rounded-3xl overflow-hidden aspect-[4/5] bg-[#12367F]/10 group">
            <img
              src={item.image}
              alt={item.title}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
            />
          </div>
        </div>
        <div className={`lg:col-span-6 ${reverse ? 'lg:order-1' : ''}`}>
          <p className="text-xs md:text-sm uppercase tracking-[0.25em] text-[#12367F]/60 font-medium mb-5">
            0{index + 1} / 0{offerings.length}
          </p>
          <h3
            className="font-display uppercase text-[#12367F]"
            style={{ fontSize: 'clamp(2rem, 4.5vw, 4rem)', lineHeight: 0.95 }}
          >
            {item.title}
          </h3>
          <div className="mt-6 space-y-5 text-[#12367F]/85 text-base md:text-lg leading-relaxed">
            {item.body.map((p, i) => <p key={i}>{p}</p>)}
          </div>
          <div className="mt-8">
            <a href={item.cta.href} className="btn-base btn-accent">
              {item.cta.label} <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const Offerings = () => {
  return (
    <main className="bg-white text-[#12367F]">
      <Navbar />
      <OfferingsHero />
      <OfferingsIntro />
      <div className="divide-y divide-[#12367F]/10">
        {offerings.map((o, i) => (
          <OfferingRow key={i} item={o} index={i} />
        ))}
      </div>
      <Blog />
      <FooterCTA />
      <Footer />
    </main>
  );
};

export default Offerings;
